import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getSaturationColor } from './utils';

function KeyMetricsCards({ summary }) {
  const { t, i18n } = useTranslation();
  if (!summary) return null;

  const mostSaturated = i18n.language === 'vi' ? summary.most_saturated?.vi : summary.most_saturated?.en;
  const leastSaturated = i18n.language === 'vi' ? summary.least_saturated?.vi : summary.least_saturated?.en;

  const avgInfo = getSaturationColor(summary.avg_saturation);
  const maxInfo = getSaturationColor(summary.max_saturation);
  const minInfo = getSaturationColor(summary.min_saturation);

  const dist = summary.distribution || {};
  const distItems = [
    { label: t('saturation.levels.extreme', 'Cực cao'), count: dist.extreme || 0, color: '#d32f2f' },
    { label: t('saturation.levels.high', 'Cao'), count: dist.high || 0, color: '#f57c00' },
    { label: t('saturation.levels.moderate', 'Trung bình'), count: dist.moderate || 0, color: '#fbc02d' },
    { label: t('saturation.levels.low', 'Thấp'), count: dist.low || 0, color: '#388e3c' },
  ];

  const cardSx = {
    p: 2.5,
    borderRadius: 2.5,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
    transition: 'box-shadow 0.2s',
    '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  };

  const labelSx = {
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontSize: '0.68rem',
    lineHeight: 1.4,
  };

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
      gap: { xs: 1.5, md: 2 },
      mb: { xs: 2, md: 2.5 },
    }}>
      {/* Average Score */}
      <Box sx={cardSx}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={labelSx}>
          {t('saturation.cards.averageSaturation', 'Bão hòa trung bình')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: avgInfo.color, lineHeight: 1.1, fontFeatureSettings: '"tnum"' }}>
            {summary.avg_saturation?.toFixed(1)}
          </Typography>
          <Typography variant="body2" color="text.disabled" fontWeight={500}>/100</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          {t('saturation.cards.acrossSectors', { count: summary.total_sectors })}
        </Typography>
      </Box>

      {/* Distribution */}
      <Box sx={cardSx}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={labelSx}>
          {t('saturation.overview.marketDistribution', 'Phân bổ thị trường')}
        </Typography>
        {/* Stacked bar */}
        <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', mt: 0.5, mb: 0.25 }}>
          {distItems.map((d, i) => d.count > 0 && (
            <Box key={i} sx={{ flex: d.count, bgcolor: d.color, transition: 'flex 0.4s' }} />
          ))}
        </Box>
        {/* Legend dots */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {distItems.map((d, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: d.color, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontFeatureSettings: '"tnum"', fontSize: '0.7rem' }}>
                {d.count}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Highest Risk */}
      <Box sx={{ ...cardSx, borderColor: maxInfo.color + '30', bgcolor: maxInfo.bg }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={labelSx}>
          {t('saturation.cards.highRiskSector', 'Rủi ro cao nhất')}
        </Typography>
        <Typography variant="subtitle2" fontWeight={700} color="text.primary" noWrap title={mostSaturated}>
          {mostSaturated}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: maxInfo.color, lineHeight: 1.1, fontFeatureSettings: '"tnum"' }}>
            {summary.max_saturation?.toFixed(1)}
          </Typography>
          <Typography variant="caption" color="text.disabled">/100</Typography>
        </Box>
      </Box>

      {/* Best Opportunity */}
      <Box sx={{ ...cardSx, borderColor: minInfo.color + '30', bgcolor: minInfo.bg }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={labelSx}>
          {t('saturation.cards.goodOpportunity', 'Cơ hội tốt nhất')}
        </Typography>
        <Typography variant="subtitle2" fontWeight={700} color="text.primary" noWrap title={leastSaturated}>
          {leastSaturated}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: minInfo.color, lineHeight: 1.1, fontFeatureSettings: '"tnum"' }}>
            {summary.min_saturation?.toFixed(1)}
          </Typography>
          <Typography variant="caption" color="text.disabled">/100</Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default KeyMetricsCards;
